import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { errMsg, useApi } from "@/lib/api";

const lines = (t) => t.split("\n").map((x) => x.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, "").trim()).filter(Boolean);
const toLines = (xs) => (xs || []).join("\n");

const fmtBlocks = (xs, head, extra) => (xs || []).map((o) => [`## ${head.map((h) => o[h] || "").join(" | ")}`, ...(o[extra] || []).map((x) => `- ${x}`),
  ...(o.bahan ? [`Bahan: ${o.bahan}`] : [])].join("\n")).join("\n\n");
const parseBlocks = (t, head, extra) => t.split(/^##\s*/m).map((b) => b.trim()).filter(Boolean).map((b) => {
  const [first, ...rest] = b.split("\n");
  const parts = first.split("|").map((x) => x.trim());
  const o = Object.fromEntries(head.map((h, i) => [h, parts[i] || ""]));
  const bahan = rest.find((x) => /^bahan:/i.test(x.trim()));
  if (head.includes("kaedah")) o.bahan = bahan ? bahan.replace(/^\s*bahan:\s*/i, "") : "";
  o[extra] = lines(rest.filter((x) => x !== bahan).join("\n"));
  return o;
});

const KAND = ["sesi", "masa", "lapis"], AKT = ["nama", "masa", "kaedah"], SLD = ["tajuk"];
const FIELDS = [["analisis_keperluan", "Analisis keperluan", 3], ["objektif", "Objektif (satu baris satu objektif)", 4],
  ["rangka_kandungan", "Rangka kandungan (## Sesi | masa | lapis, kemudian - isi)", 10], ["aktiviti", "Aktiviti (## Nama | masa | kaedah, - langkah, Bahan: …)", 10],
  ["soalan_refleksi", "Soalan refleksi (satu baris satu soalan)", 5], ["rangka_slaid", "Rangka slaid (## Tajuk slaid, kemudian - poin)", 10],
  ["tindakan_selepas", "Tindakan selepas program", 4], ["amanah", "Amanah 7 hari", 2]];

const toForm = (l, amanah) => ({ analisis_keperluan: l.analisis_keperluan, objektif: toLines(l.objektif), soalan_refleksi: toLines(l.soalan_refleksi),
  tindakan_selepas: toLines(l.tindakan_selepas), rangka_kandungan: fmtBlocks(l.rangka_kandungan, KAND, "isi"),
  aktiviti: fmtBlocks(l.aktiviti, AKT, "langkah"), rangka_slaid: fmtBlocks(l.rangka_slaid, SLD, "poin"), amanah });
const fromForm = (f) => ({ analisis_keperluan: f.analisis_keperluan.trim(), objektif: lines(f.objektif), soalan_refleksi: lines(f.soalan_refleksi),
  tindakan_selepas: lines(f.tindakan_selepas), rangka_kandungan: parseBlocks(f.rangka_kandungan, KAND, "isi"),
  aktiviti: parseBlocks(f.aktiviti, AKT, "langkah"), rangka_slaid: parseBlocks(f.rangka_slaid, SLD, "poin") });

export const LatihanEditor = ({ kajian, onSaved }) => {
  const api = useApi();
  const d = kajian.current;
  const [f, setF] = useState(() => toForm(d.latihan, d.amanah));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { onSaved(await api("post", `/hte/kajian/${kajian.id}/sunting`, { latihan: fromForm(f), amanah: f.amanah })); toast.success("Disimpan sebagai versi baharu"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="space-y-4" data-testid="latihan-editor">
      <p className="text-sm text-mute">Sunting secara manual. Setiap simpanan menjadi versi baharu dan versi lama boleh dipulihkan. Tiada kuota digunakan.</p>
      {FIELDS.map(([k, label, rows]) => (
        <label key={k} className="block">
          <span className="eyebrow block mb-2">{label}</span>
          <textarea rows={rows} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-${k}`}
            className="w-full px-4 py-3 rounded-xl bg-bg border border-line text-sm text-ink font-mono-data leading-relaxed focus:outline-none focus:border-[var(--q)] resize-y" />
        </label>
      ))}
      <button onClick={save} disabled={busy} data-testid="latihan-save-button"
        className="h-11 px-6 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Simpan suntingan
      </button>
    </section>
  );
};
